import { Dispatch, SetStateAction, useMemo, useState } from 'react'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'
import { Badge } from '~/components/UI/Badge'
import { formatMoney } from '~/helpers'
import { countSelectedFaces } from './helpers'

import {
  Flex,
  Text,
  Menu,
  MenuItem,
  IconPencil,
  IconTrash,
  Modal,
  ModalHeader,
  ModalHeading,
  ModalContent,
  ModalFooter,
  ModalDismiss,
  Button
} from '@vtex/shoreline'

import screensImage from '~/assets/img/elements/screens.png'
import s from './styles.module.css'

interface ScreenGroupProps {
  group: ScreenGroup
  setScreenGroups: Dispatch<SetStateAction<ScreenGroup[]>>
  handleEditGroup: () => void
  isStandard?: boolean
}

export default function ScreenGroup({
  group,
  setScreenGroups,
  handleEditGroup,
  isStandard
}: ScreenGroupProps) {
  const { t } = useAPPTranslation()
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)

  const averageCpm = useMemo(
    () =>
      group?.screens?.reduce((acc, screen) => acc + Number(screen.price), 0) /
        group?.screens?.length || 0,
    [group]
  )

  function handleDeleteGroup() {
    setScreenGroups(prevGroups => prevGroups.filter(g => g !== group))
    setIsDeleteModalOpen(false)
  }

  return (
    <Flex gap="var(--sl-space-3)" align="start" className={s.groupContainer}>
      <img src={screensImage} alt="screen" width={32} className={s.image} />
      <Flex direction="column" gap="var(--sl-space-2)">
        <Flex gap="var(--sl-space-0)">
          {group?.tags?.map(tag => (
            <Badge key={tag}>{tag}</Badge>
          ))}
        </Flex>
        <Text variant="body" className={s.textSoft}>
          {t('rm:campaign.create.ScreenGroups.quantityScreens', {
            count: countSelectedFaces(group?.screens)
          })}
        </Text>
        {isStandard && (
          <Text variant="caption2" className={s.textSoft}>
            {t('rm:campaign.create.ScreenGroups.averageCpm', {
              averageCpm: formatMoney(averageCpm)
            })}
          </Text>
        )}
      </Flex>
      <Menu
        iconOnly
        label="actions"
        type="actions"
        className={s.marginLeftAuto}
      >
        <MenuItem onClick={handleEditGroup}>
          <IconPencil />
          {t('rm:campaign.create.ScreenGroups.edit')}
        </MenuItem>
        <MenuItem
          onClick={() => setIsDeleteModalOpen(true)}
          className={s.buttonCritical}
        >
          <IconTrash />
          {t('rm:campaign.create.ScreenGroups.delete')}
        </MenuItem>
      </Menu>

      {/* Modal to confirm group deletion */}
      <Modal
        size="small"
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
      >
        <ModalHeader>
          <ModalHeading>
            {t('rm:campaign.create.ScreenGroups.DeleteModal.title')}
          </ModalHeading>
          <ModalDismiss />
        </ModalHeader>
        <ModalContent>
          {t('rm:campaign.create.ScreenGroups.DeleteModal.description')}
        </ModalContent>
        <ModalFooter>
          <Button onClick={() => setIsDeleteModalOpen(false)}>
            {t('rm:button.Cancel')}
          </Button>
          <Button variant="critical" onClick={handleDeleteGroup}>
            {t('rm:campaign.create.ScreenGroups.delete')}
          </Button>
        </ModalFooter>
      </Modal>
    </Flex>
  )
}
